import axios from 'axios'
import store from '../store'
import { constants } from 'app-constants'

import { message, notification } from 'antd'

export const baseUrl = process.env.REACT_APP_API_BASE + '/api/v1/'

// auth endpoints
export const baseAuth = baseUrl + 'auth/'
export const loginUrl = baseAuth + 'login/'
export const logoutUrl = baseAuth + 'logout/'
export const signupUrl = baseAuth + 'signup/'
export const authTokenUrl = baseAuth + 'signup/token-auth/'
export const verifyAccountUrl = baseAuth + 'signup/verify-email/'
export const passwordChangeUrl = baseAuth + 'password/change/'
export const refreshTokenUrl = baseAuth + 'refresh-token/'

// users urls
export const adminUsersUrl = baseUrl + 'accounts/admin/users'
export const usersUrl = baseUrl + 'accounts/users/'
export const verifyUserStatusUrl = usersUrl + 'get-user-status'
export const OTPUrl = usersUrl + 'send-otp'
export const OTPVerificationUrl = usersUrl + 'verify-otp'
export const passcodeResetUrl = usersUrl + 'reset-password'
export const loginWithPasscodeUrl = usersUrl + 'login-with-password'
export const checkPhoneNumberUrl = usersUrl + 'check-phone-number'
export const signupWithPasscodeUrl = usersUrl + 'register-with-password'

// cars urls
export const carsUrl = baseUrl + 'cars/admin/cars'
export const carOrdersUrl = baseUrl + 'cars/admin/car-orders'
export const carImagesUrl = baseUrl + 'cars/admin/car-images'
export const customDutiesUrl = baseUrl + 'custom-duties/admin/car-details'
// AI-assisted create: creates a request and synchronously fetches the MSRP via AI when
// it is left blank (custom-duty AI MSRP estimation v1).
export const customDutiesAiCreateUrl = customDutiesUrl + '/ai-create/'
// Regenerate (delete + recreate) the customer-facing PDF duty document for a request,
// rebuilding it from the latest exchange rate and the request's current values.
export const customDutyRegenerateDocumentUrl = id =>
  `${customDutiesUrl}/${id}/regenerate-document/`
// Body types owned by the custom-duties app (separate table from core/main body types).
// The car-details endpoint validates body_type against this table, so the custom-duties
// form must pull its options from here, not the core body-types endpoint.
export const customDutyBodyTypesUrl = baseUrl + 'custom-duties/admin/body-types'
// Shipping fees (freight + insurance) per (origin, body type). The WhatsApp assistant
// auto-onboards not-yet-seeded markets with AI-estimated, review-flagged fees; this is the
// Shipping_Fee_Review contract (w-api customs-duty-assistant-enhancements Req 3.7).
// List supports a `?flagged=true` filter; only GET (list) + PATCH are allowed.
export const shippingFeesUrl = baseUrl + 'custom-duties/admin/shipping-fees'
// Customs MSRP whitelist — curated customs HDV values that override the AI MSRP lookup for
// matching cars (make+model+year+origin + optional trim/fuel/drive/engine discriminators). CRUD.
export const dutyMsrpWhitelistUrl = baseUrl + 'custom-duties/admin/msrp-whitelist'
export const shippingFeeUrl = id => `${shippingFeesUrl}/${id}/`
// The two hand-entered rate tables (w-api spec 0076): banking rates price duty estimates, car rates
// price the listings. Each has current/, a paginated history, POST to save (a new row) and
// <id>/restore/.
export const dutyBankingRatesUrl = baseUrl + 'custom-duties/admin/banking-rates'
export const carRatesUrl = baseUrl + 'core/admin/exchange-rates'

// duty assistant (WhatsApp) admin urls — w-api Admin_Management contract (w-api design §9).
// These paths/verbs/shapes are fixed by the contract; do not diverge.
export const dutyKbUrl = baseUrl + 'duty-assistant/admin/knowledge-entries'
export const dutyPricingUrl = baseUrl + 'duty-assistant/admin/body-type-prices'
// B2B dealer accounts: discounted duty-service pricing per dealer (flat % + optional per-body-type
// override). Full CRUD; phone is normalised server-side.
export const dutyDealersUrl = baseUrl + 'duty-assistant/admin/dealers'
export const dutyConversationsUrl = baseUrl + 'duty-assistant/admin/conversations'

// Car requests from the app: what a customer wants when nothing in stock is it, and the budget
// they have ready. Served from `api/v1/app-admin/`, NOT from `api/v1/app/` — everything under the
// latter is device-scoped and anonymous-capable, and the API's own guard tests enforce that.
export const carRequestsUrl = baseUrl + 'app-admin/car-requests'
// Send a free-form message straight to the customer from the dashboard (no processing).
export const dutyConversationMessageUrl = id =>
  `${dutyConversationsUrl}/${id}/message/`
// Steps an admin can re-send on a CONVERSATION. Duty's steps act on a car and live on the
// car-requests endpoints below; vehicle history and tracking have no car, so theirs are here.
export const dutyConversationTriggerStepsUrl = service =>
  `${dutyConversationsUrl}/trigger-steps/?service=${service}`
export const dutyConversationTriggerUrl = id =>
  `${dutyConversationsUrl}/${id}/trigger/`
// Per-car-request admin edit (PATCH slot_values) + manual finalize, so support can correct a
// stuck customer's details and process the request.
export const dutyCarRequestsUrl = baseUrl + 'duty-assistant/admin/car-requests'
export const dutyCarRequestFinalizeUrl = id =>
  `${dutyCarRequestsUrl}/${id}/finalize/`
// Manual step control: (re-)trigger a specific assistant step for a car (ask a field, resend the
// Pay Now CTA, advance, etc.) so support can push a stuck/assisted customer forward. The
// companion list endpoint returns the available steps + labels so the UI stays in sync.
export const dutyCarRequestTriggerUrl = id =>
  `${dutyCarRequestsUrl}/${id}/trigger/`
export const dutyCarRequestTriggerStepsUrl = `${dutyCarRequestsUrl}/trigger-steps/`
// Duty-service payments (purpose=DUTY-PAYMENT) + dashboard analytics — w-api duty-service
// analytics/transactions contract. List/create/patch (no delete); filterable by status/date/search.
export const dutyTransactionsUrl = baseUrl + 'duty-assistant/admin/transactions'
// Overall successful revenue for the Transactions page — the total across ALL payments, narrowed
// only when a from/to date filter is set (not the loaded-page sum).
export const dutyTransactionSummaryUrl = `${dutyTransactionsUrl}/summary/`
// Log a duty payment made OUTSIDE the assistant (walk-in / DM customer who paid directly).
export const dutyTransactionLogManualUrl = `${dutyTransactionsUrl}/log-manual/`
// On-demand, gateway-agnostic payment-attempt timeline for one transaction (how the customer
// tried to pay + where they got stuck). Fetched only when an admin opens it, for unpaid txns.
export const dutyTransactionTimelineUrl = id =>
  `${dutyTransactionsUrl}/${id}/timeline/`
export const dutyAnalyticsUrl = baseUrl + 'duty-assistant/admin/analytics/'
// Customer analytics — one row per WhatsApp duty customer with activity aggregates (duty
// requests/completed/paid/revenue), a registered-dealer flag, and a nested detail (their
// conversations/requests/payments). Read-only; searchable/orderable/filterable.
export const dutyCustomersUrl = baseUrl + 'duty-assistant/admin/customers'
export const dutyCustomerDetailUrl = id => `${dutyCustomersUrl}/${id}/`
// Broadcast messages — compose a WhatsApp marketing/announcement message once and deliver it to a
// filtered/hand-picked/manual set of existing customers, with delivery + response tracking (spec:
// broadcast-messages). CRUD + workflow actions on the broadcasts resource, plus a stateless AI
// drafting endpoint.
export const dutyBroadcastsUrl = baseUrl + 'duty-assistant/admin/broadcasts'
export const dutyBroadcastConfigUrl = `${dutyBroadcastsUrl}/config/`
export const dutyBroadcastDetailUrl = id => `${dutyBroadcastsUrl}/${id}/`
export const dutyBroadcastCandidatesUrl = id => `${dutyBroadcastsUrl}/${id}/candidates/`
export const dutyBroadcastAddRecipientsUrl = id =>
  `${dutyBroadcastsUrl}/${id}/add-recipients/`
export const dutyBroadcastAddSegmentUrl = id => `${dutyBroadcastsUrl}/${id}/add-segment/`
export const dutyBroadcastRemoveRecipientsUrl = id =>
  `${dutyBroadcastsUrl}/${id}/remove-recipients/`
export const dutyBroadcastRecipientsUrl = id => `${dutyBroadcastsUrl}/${id}/recipients/`
export const dutyBroadcastRetryRecipientsUrl = id =>
  `${dutyBroadcastsUrl}/${id}/retry-recipients/`
export const dutyBroadcastScheduleUrl = id => `${dutyBroadcastsUrl}/${id}/schedule/`
export const dutyBroadcastSendNowUrl = id => `${dutyBroadcastsUrl}/${id}/send-now/`
export const dutyBroadcastPauseUrl = id => `${dutyBroadcastsUrl}/${id}/pause/`
export const dutyBroadcastResumeUrl = id => `${dutyBroadcastsUrl}/${id}/resume/`
export const dutyBroadcastStatsUrl = id => `${dutyBroadcastsUrl}/${id}/stats/`
export const dutyBroadcastAiDraftUrl =
  baseUrl + 'duty-assistant/admin/broadcasts/ai-draft/'
// Promotions — time-boxed % discount off the duty service fee (spec: promotions). CRUD; the list
// supports ?live=true. Applied automatically server-side; this manages them + links to broadcasts.
export const dutyPromotionsUrl = baseUrl + 'duty-assistant/admin/promotions'
export const dutyPromotionDetailUrl = id => `${dutyPromotionsUrl}/${id}/`
// Drive-train badges — the curated dictionary (4MATIC, xDrive, quattro, sDrive) the assistant
// reads a car's drive train from instead of asking whether it is AWD (spec:
// duty-drive-train-intelligence).
export const dutyDriveTrainBadgesUrl =
  baseUrl + 'duty-assistant/admin/drive-train-badges'
// Payment-flow switch (web LINK vs in-chat mobile money). GET + PATCH the singleton config.
export const dutyPaymentConfigUrl =
  baseUrl + 'duty-assistant/admin/payment-config/'
// The assistant's switches, timings and audience rules — one settings row (spec:
// duty-assistant-intake-ux, extended by duty-follow-up-messages). GET + PATCH one field at a time.
export const dutyAssistantConfigUrl =
  baseUrl + 'duty-assistant/admin/assistant-config/'
// What the intake changes did, for a window of days (spec: duty-assistant-intake-ux §9).
export const dutyIntakeReportUrl = baseUrl + 'duty-assistant/admin/intake-report/'
// Follow-up messages (spec: duty-follow-up-messages): the four editable messages, a preview that
// renders a draft without saving it, the report, and the customers who said yes to dealer pricing.
export const dutyFollowUpMessagesUrl =
  baseUrl + 'duty-assistant/admin/follow-ups/messages/'
export const dutyFollowUpMessageUrl = kind => `${dutyFollowUpMessagesUrl}${kind}/`
export const dutyFollowUpPreviewUrl = kind =>
  `${dutyFollowUpMessagesUrl}${kind}/preview/`
export const dutyFollowUpResetUrl = kind => `${dutyFollowUpMessagesUrl}${kind}/reset/`
export const dutyFollowUpReportUrl =
  baseUrl + 'duty-assistant/admin/follow-ups/report/'
export const dutyFollowUpDealerInterestUrl =
  baseUrl + 'duty-assistant/admin/follow-ups/dealer-interest/'

// core urls
export const coreBaseUrl = baseUrl + 'core/admin/'
export const shippingCompaniesUrl = coreBaseUrl + 'shipping-companies'
export const currenciesUrl = coreBaseUrl + 'currencies'
export const originsUrl = coreBaseUrl + 'origins'
export const bodyTypesUrl = coreBaseUrl + 'body-types'
export const transmissionsUrl = coreBaseUrl + 'transmissions'
export const fuelTypesUrl = coreBaseUrl + 'fuel-types'
export const carMakesUrl = coreBaseUrl + 'car-makes'
export const carModelsUrl = coreBaseUrl + 'car-models'
export const driveTrainsUrl = coreBaseUrl + 'drive-trains'
export const conditionsUrl = coreBaseUrl + 'conditions'
export const statusesUrl = coreBaseUrl + 'statuses'
export const carSpecsUrl = coreBaseUrl + 'car-specs'
export const carTasksUrl = coreBaseUrl + 'car-tasks'

// payments urls
// --- Insurance (specs: insurance-quote-and-purchase / -fulfilment / -commission-settlement) ---
// Its own prefix, outside `api/v1/app/`: that namespace is device-scoped and anonymous-capable,
// and its guard tests enumerate it to prove no admin route hides in there.
// Look up one saved car's MSRP through the duty assistant's own resolver — the curated customs
// whitelist first, then the VALIDATED AI lookup with its currency audit and corrective retry.
// Deliberately not `lookup-msrp`, which searches old requests and inventory with the AI off.
export const dutyCheckMsrpUrl = id => `${customDutiesUrl}/${id}/check-msrp/`

export const insuranceBaseUrl = baseUrl + 'insurance-admin/'
export const insurancePoliciesUrl = insuranceBaseUrl + 'policies'
export const insurancePolicyCountsUrl = insuranceBaseUrl + 'policies/counts/'
export const insurancePolicyActionUrl = (id, action) =>
  `${insurancePoliciesUrl}/${id}/${action}/`
export const insuranceVehicleClassesUrl = insuranceBaseUrl + 'vehicle-classes'
export const insurancePolicyTypesUrl = insuranceBaseUrl + 'policy-types'
export const insurersUrl = insuranceBaseUrl + 'insurers'
export const insurerLogoUrl = id => `${insurersUrl}/${id}/logo/`
export const insurancePricingUrl = insuranceBaseUrl + 'pricing'
export const insuranceConfigUrl = insuranceBaseUrl + 'config'
export const insuranceBrokersUrl = insuranceBaseUrl + 'brokers'
export const insuranceSetupHealthUrl = insuranceBaseUrl + 'setup/health/'
export const insuranceSettlementSummaryUrl = insuranceBaseUrl + 'settlement/summary/'
export const insuranceLedgerUrl = insuranceBaseUrl + 'ledger'
export const insuranceInvoicesUrl = insuranceBaseUrl + 'invoices'
export const insuranceRemittancesUrl = insuranceBaseUrl + 'remittances'
export const insuranceBatchActionUrl = (kind, id, action) =>
  `${insuranceBaseUrl}${kind}/${id}/${action}/`

// --- The agent desk (spec: insurance-agent-desk) ---------------------------------------------
// Starting and working a policy on the dashboard while talking to the customer, because the app
// is not published yet and policies are being sold today.
//
// Built here, never through `formatUrl`. That helper is for pagination URLs the SERVER returned;
// running a self-built URL through it is what produced `httpss://` and an insurance page that
// silently sent no request at all.
export const insuranceStartPolicyUrl = insuranceBaseUrl + 'policies/start/'
export const insurancePolicyCardsUrl = id => `${insurancePoliciesUrl}/${id}/cards/`
export const insurancePolicyChooseUrl = id => `${insurancePoliciesUrl}/${id}/choose/`
export const insurancePolicyDetailsUrl = id => `${insurancePoliciesUrl}/${id}/details/`
export const insurancePolicyPaymentUrl = id => `${insurancePoliciesUrl}/${id}/payment/`
export const insurancePolicyDocumentsUrl = id => `${insurancePoliciesUrl}/${id}/documents/`
export const insuranceDocumentUrl = (id, docId) =>
  `${insurancePoliciesUrl}/${id}/documents/${docId}/`
export const insuranceDocumentSendUrl = (id, docId) =>
  `${insurancePoliciesUrl}/${id}/documents/${docId}/send/`
// Our own account details per rail — the numbers an agent reads out. From the same table the app
// would have shown, so an account that changed last month changed in one place.
export const insuranceRailsUrl = (country = 'GH') =>
  `${insuranceBaseUrl}rails/?country=${country}`

// --- Shipment tracking (spec: shipment-tracking) ---------------------------------------------
// Staff-only, same permission class as insurance-admin. Built here, never through `formatUrl`.
export const trackingAdminBaseUrl = baseUrl + 'tracking-admin/'
export const trackingShipmentsUrl = trackingAdminBaseUrl + 'shipments/'
export const trackingShipmentUrl = id => `${trackingShipmentsUrl}${id}/`
// action: 'retrack' | 'resolve' | 'close' | 'actions'
export const trackingShipmentActionUrl = (id, action) => `${trackingShipmentsUrl}${id}/${action}/`
export const trackingLinesUrl = trackingAdminBaseUrl + 'lines/'
export const trackingLineUrl = id => `${trackingLinesUrl}${id}/`
// The country travels in both the query and the body on PUT, so either server reading works.
export const trackingPricingUrl = (country = 'GH') =>
  `${trackingAdminBaseUrl}pricing/?country=${country}`
export const trackingAccountUrl = trackingAdminBaseUrl + 'account/'
// Recording the ShipsGo balance by hand. ShipsGo has no balance endpoint — the number rides on a
// response header, and the only call that reads one is a shipment create — so between creates our
// copy goes stale and a top-up cannot be noticed.
export const trackingAccountCreditsUrl = trackingAdminBaseUrl + 'account/credits/'
// A bill of lading waiting for a person is keyed on the CONVERSATION, not on a shipment: a
// hand-off happens because the thing that would have created a shipment is exactly what failed
// (spec: whatsapp-shipment-tracking Req 1.2).
export const trackingBillOfLadingUrl = trackingAdminBaseUrl + 'bill-of-lading/'
export const trackingBillOfLadingCompleteUrl = conversationId =>
  `${trackingBillOfLadingUrl}${conversationId}/complete/`
// A POST, because it MINTS a link: a signed URL lives minutes, and one kept in a payload would
// outlive the page it was built for.
export const trackingDocumentLinkUrl = documentId =>
  `${trackingBillOfLadingUrl}documents/${documentId}/link/`

// --- Vehicle history reports (spec: vehicle-history-reports) ---------------------------------
// Staff-only, same permission class as tracking-admin. This is the ONLY layer of the product that
// may show a cost or a margin: the customer API walks its own JSON to prove neither leaks, and the
// owner cannot price a report without seeing both.
export const vehicleHistoryAdminBaseUrl = baseUrl + 'vehicle-history-admin/'
export const vehicleHistoryProductsUrl = vehicleHistoryAdminBaseUrl + 'products/'
export const vehicleHistoryProductUrl = id => `${vehicleHistoryProductsUrl}${id}/`
export const vehicleHistoryProductPreviewUrl = id =>
  `${vehicleHistoryProductsUrl}${id}/preview/`
export const vehicleHistoryServicesUrl = vehicleHistoryAdminBaseUrl + 'supplier-services/'
export const vehicleHistoryServiceUrl = id => `${vehicleHistoryServicesUrl}${id}/`
export const vehicleHistoryAccountUrl = vehicleHistoryAdminBaseUrl + 'account/'
export const vehicleHistoryAccountRefreshUrl =
  vehicleHistoryAdminBaseUrl + 'account/refresh/'
export const vehicleHistoryPriceChangesUrl = vehicleHistoryAdminBaseUrl + 'price-changes/'
export const vehicleHistoryAlertsUrl = vehicleHistoryAdminBaseUrl + 'alerts/'
export const vehicleHistoryAlertClearUrl = id =>
  `${vehicleHistoryAlertsUrl}${id}/clear/`
export const vehicleHistoryOrdersUrl = vehicleHistoryAdminBaseUrl + 'orders/'
export const vehicleHistoryOrderUrl = id => `${vehicleHistoryOrdersUrl}${id}/`
export const vehicleHistoryOrderActionsUrl = id => `${vehicleHistoryOrdersUrl}${id}/actions/`
export const vehicleHistorySummaryUrl = (days = 30) =>
  `${vehicleHistoryAdminBaseUrl}summary/?days=${days}`

// --- Inventory assistant (spec: inventory-assistant) -----------------------------------------
// The Telegram bot that manages cars. Staff-only. The dashboard only READS what the bot did and
// manages who may command it; it never writes to a car through these.
export const inventoryAdminBaseUrl = baseUrl + 'inventory/admin/'
export const inventoryChangesUrl = inventoryAdminBaseUrl + 'changes/'
export const inventoryPeopleUrl = inventoryAdminBaseUrl + 'telegram-users/'
export const inventoryPersonUrl = id => `${inventoryPeopleUrl}${id}/`
export const inventoryUsageUrl = inventoryAdminBaseUrl + 'ai-usage/'
export const inventoryStatusUrl = inventoryAdminBaseUrl + 'status/'

export const paymentsUrl = baseUrl + 'payments/admin/'
export const transactionsUrl = paymentsUrl + 'transactions'

// Which company moves the money, per service and rail (spec: payment-gateway-routing). A RAIL is
// how the customer pays and was already per-service data; a GATEWAY is the company that moves it
// on that rail, and until this spec there was no such question — Paystack was the only answer.
//
// `routes/` answers both halves at once: what somebody TYPED and who would actually take the next
// payment after the server's fallbacks. The difference between those two is the whole reason this
// page exists, so never show one without the other.
export const paymentRoutesUrl = baseUrl + 'payments-admin/routes/'
export const paymentRouteUrl = (service, country, rail) =>
  `${paymentRoutesUrl}${service}/${country}/${rail}/`
// Every gateway this build can route to, with whether it is set up here and which rails it serves.
export const paymentGatewaysUrl = baseUrl + 'payments-admin/gateways/'
// GET says what a refund WOULD be (how much is left, which company, whether it can be sent at
// all); POST performs one. A gateway that cannot refund records a row for a person to action
// instead — the button says so rather than failing.
export const transactionRefundUrl = reference =>
  `${baseUrl}payments-admin/transactions/${reference}/refund/`

export const getHeaders = () => {
  axios.defaults.xsrfCookieName = 'csrftoken'
  axios.defaults.xsrfHeaderName = 'X-CSRFToken'

  const authUser = store.getState().authUser
  const headers = {
    'Content-Type': 'application/json',
    Authorization: authUser ? 'Bearer ' + authUser.access : ''
  }
  return headers
}

export const getFormHeaders = () => {
  axios.defaults.xsrfCookieName = 'csrftoken'
  axios.defaults.xsrfHeaderName = 'X-CSRFToken'

  const authUser = store.getState().authUser
  const headers = {
    'Content-Type': 'multipart/form-data',
    Authorization: authUser ? 'Bearer ' + authUser.access : ''
  }
  return headers
}

/**
 * Upgrade a server-supplied URL to https in production.
 *
 * It exists for DRF pagination links: behind a proxy that does not set X-Forwarded-Proto, `next`
 * and `previous` come back as http, and the browser blocks them as mixed content.
 *
 * It used to be `url.replace(/http/g, 'https')` — a GLOBAL replace of the bare word. Given a URL
 * that was already https, that produced `httpss://`, which the browser rejects as an unsupported
 * protocol BEFORE opening a connection: no entry in the network tab, just a failed promise. It
 * only ever bit a caller who passed a URL built from `baseUrl` rather than one the server sent
 * back, which is why it survived this long.
 *
 * Anchored and scheme-only now, so it is safe to call on any URL, once or twice.
 */
export const formatUrl = url => {
  if (!url) return url
  if (process.env.NODE_ENV === 'production') {
    return url.replace(/^http:\/\//i, 'https://')
  }
  return url
}

export const successMessage = msg => {
  message.success(msg)
}

export const errorMessage = msg => {
  message.error(msg)
}

export const warningMessage = msg => {
  message.warning(msg)
}

export const successNotification = msg => {
  notification.success({
    message: msg,
    description: msg
  })
}

export const errorNotification = msg => {
  notification.error({
    message: msg,
    description: msg
  })
}

export const warningNotification = msg => {
  notification.warning({
    message: msg,
    description: msg
  })
}

export const infoNotification = msg => {
  notification.info({
    message: msg,
    description: msg
  })
}

export const errorToast = (toast, message, error, props) => {
  if (error.response) {
    if (error.response.status === 401) {
      toast.info('Session Expired, Login Again', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true
      })
      // remove expired creds
      store.dispatch({
        type: constants.REMOVE_USER
      })
      localStorage.removeItem('wabenzyAdmin')
      store.dispatch({
        type: constants.REMOVE_SETTINGS
      })
      localStorage.removeItem('settings')
      // go to login page
      props.history.push('/auth/login')
    } else {
      toast.error(message, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true
      })
    }
  } else {
    // eslint-disable-next-line quotes
    toast.error("can't connect to server, check internet connection.", {
      position: 'top-center',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true
    })
  }
}

export const successToast = (toast, message) => {
  toast.success(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true
  })
}

export const infoToast = (toast, message) => {
  toast.info(message, {
    position: 'top-right',
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true
  })
}

export const getRequest = async (url, dataName, setLoading) => {
  setLoading(true)
  const response = {}
  try {
    const res = await axios.get(url, {
      headers: getHeaders()
    })
    response.success = true
    response.data = res.data
  } catch (err) {
    errorNotification('Error getting ' + dataName + ', retry.')
    response.success = false
    response.error = err
  } finally {
    setLoading(false)
  }
  return response
}

export const postRequest = async (
  url,
  payload,
  dataName,
  setLoading,
  defaultHeaders = true
) => {
  setLoading(true)
  const response = {}
  try {
    const res = await axios.post(url, payload, {
      headers: defaultHeaders ? getHeaders() : getFormHeaders()
    })
    response.success = true
    response.data = res.data
  } catch (err) {
    errorNotification('Error creating ' + dataName + ', retry.')
    response.success = false
    response.error = err
  } finally {
    setLoading(false)
  }
  return response
}

export const putRequest = async (url, payload, dataName, setLoading) => {
  setLoading(true)
  const response = {}
  try {
    const res = await axios.put(url, payload, {
      headers: getHeaders()
    })
    response.success = true
    response.data = res.data
  } catch (err) {
    errorNotification('Error updating ' + dataName + ', retry.')
    response.success = false
    response.error = err
  } finally {
    setLoading(false)
  }
  return response
}

export const patchRequest = async (url, payload, dataName, setLoading) => {
  setLoading(true)
  const response = {}
  try {
    const res = await axios.patch(url, payload, {
      headers: getHeaders()
    })
    response.success = true
    response.data = res.data
  } catch (err) {
    errorNotification('Error updating ' + dataName + ', retry.')
    response.success = false
    response.error = err
  } finally {
    setLoading(false)
  }
  return response
}

export const deleteRequest = async (url, dataName, setLoading) => {
  setLoading(true)
  const response = {}
  try {
    const res = await axios.delete(url, {
      headers: getHeaders()
    })
    response.success = true
    response.data = res.data
  } catch (err) {
    errorNotification('Error deleting ' + dataName + ', retry.')
    response.success = false
    response.error = err
  } finally {
    setLoading(false)
  }
  return response
}

// --- Growth bot (w-api spec 0077 Req 9) ------------------------------------------------------------
export const growthAdminBaseUrl = baseUrl + 'growth/admin/'
export const growthOpportunitiesUrl = growthAdminBaseUrl + 'opportunities/'
export const growthOpportunityUrl = id => `${growthOpportunitiesUrl}${id}/`
export const growthDecideUrl = id => `${growthOpportunitiesUrl}${id}/decide/`
export const growthPlacesUrl = growthAdminBaseUrl + 'places/'
export const growthPlaceUrl = id => `${growthPlacesUrl}${id}/`
export const growthReviewsUrl = growthAdminBaseUrl + 'reviews/'
export const growthReviewUrl = id => `${growthReviewsUrl}${id}/`
export const growthConfigUrl = growthAdminBaseUrl + 'config/'
export const growthSourcesUrl = growthAdminBaseUrl + 'sources/'
export const growthSourceUrl = id => `${growthSourcesUrl}${id}/`
export const growthRunsUrl = growthAdminBaseUrl + 'runs/'
export const growthSpendUrl = growthAdminBaseUrl + 'spend/'
export const growthRunUrl = growthAdminBaseUrl + 'run/'
export const growthStatusUrl = growthAdminBaseUrl + 'status/'
